import { BASE_URL } from "@/Store/Action";
import { Button } from "flowbite-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useState } from "react";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.locale("ko");

const TZ = "Asia/Seoul";

function Attend({
  onPrev,
  onNext,
  guideline,
}: {
  onPrev: () => void;
  onNext: () => void;
  guideline: any;
}) {
  const { secretkey } = useParams<{ secretkey: string }>();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqReason, setFaqReason] = useState("");

  const patchAttended = async () => {
    try {
      const response = await fetch(`${BASE_URL}/wg/attended`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          is_package: false,
          secret_key: secretkey,
          user_shipping_info: {
            address: "",
            address_detail: "",
            receiver_name: "",
            receiver_phone: "",
            zip_code: "",
          },
        }),
      });

      if (!response.ok) throw new Error("Failed to patch attended status.");

      return await response.json();
    } catch (err) {
      throw err;
    }
  };

  const handleAttend = () => {
    setShowConfirmation(true);
    setShowModal(false);
  };

  const confirmAttend = async () => {
    try {
      setLoading(true);
      await patchAttended();
      setLoading(false);
      setShowConfirmation(false);
      window.location.reload();
    } catch (error) {
      console.error("Failed to mark attended:", error);
      alert("처리 중 오류가 발생했습니다.");
      setLoading(false);
    }
  };

  const removeBrackets = (text: string) =>
    text.replace(/[\[\(].*?[\]\)]/g, "").trim();

  // Parsing review_start_date and review_end_date with timezone
  const startDate = guideline?.review_start_date
    ? dayjs.tz(guideline.review_start_date, "YY-MM-DD", TZ)
    : null;
  const endDate = guideline?.review_end_date
    ? dayjs.tz(guideline.review_end_date, "YY-MM-DD", TZ)
    : null;

  // Parsing visit_time "HH:mm - HH:mm"
  // let formattedVisitTime = guideline?.visit_time ?? "";
  // if (startDate && endDate && guideline?.visit_time) {
  //   const [startHourStr, endHourStr] = guideline.visit_time.split(" - ");
  //   const [startH, startM] = startHourStr.split(":").map(Number);
  //   const [endH, endM] = endHourStr.split(":").map(Number);

  //   const startDateTime = startDate.hour(startH).minute(startM);
  //   const endDateTime = endDate.hour(endH).minute(endM);

  //   formattedVisitTime = `${startDateTime.format("YYYY년 M월 D일 H시 mm분")} ~ ${endDateTime.format("YYYY년 M월 D일 H시 mm분")}`;
  // }

  const sendFAQ = async () => {
    if (!secretkey || !faqReason.trim()) {
      alert("사유를 입력해주세요.");
      return;
    }

    try {
      setFaqLoading(true);
      const response = await fetch(`${BASE_URL}/wg/faq`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: faqReason,
          secret_key: secretkey,
        }),
      });

      if (!response.ok) throw new Error("문의 전송 실패");

      alert("요청이 접수되었습니다. 위유팀에서 도와드리겠습니다.");
      setFaqReason("");
      setShowContactModal(false);
    } catch (error) {
      console.error(error);
      alert("요청 중 오류가 발생했습니다.");
    } finally {
      setFaqLoading(false);
    }
  };

  return (
    <div className="bottom-3">
      <div className="border pb-2 mb-5 flex flex-col gap-1 ps-3 bg-gray-100 rounded-lg pt-5">
        <h2 className="text-sm font-bold text-gray-800 mb-1">초대합니다</h2>
        <p className="text-xs text-red-500 mb-4">
          예약하신 시간에 방문부탁드리고, 이 초대장을 보여주세요 😊
        </p>
      </div>

      {/* Step 1 */}
      <div className="flex items-start mb-4 ml-2 gap-1">
        <div className="flex flex-col gap-3 items-center mr-2 pt-3">
          <div className="w-6 h-6 text-xs rounded-full flex items-center justify-center bg-[#46B6D0] text-white">
            1
          </div>
          <div
            className={`h-[30vh] w-[1px] ${
              guideline?.is_attend ? "bg-[#46B6D0]" : "bg-gray-200"
            }`}
          />
        </div>

        <div className="flex-1">
          <div className="flex justify-between items-center pb-2">
            <p className="text-[#46B6D0] text-sm pt-3">체험정보</p>
            {guideline.is_faq === false && (
              <button
                onClick={() => setShowContactModal(true)}
                className="bg-[#0F1E6C] py-2 px-5 rounded-full text-xs text-white"
              >
                업체와 연락이 안돼요
              </button>
            )}
            {guideline.is_faq === true && (
              <button
                disabled
                className="bg-gray-200 py-2 px-5 rounded-full text-xs text-white"
              >
                업체와 연락이 안돼요
              </button>
            )}
          </div>
          <div className="bg-[#F4F5FF] rounded-lg p-4 text-xs mt-2">
            <div className="flex flex-col gap-2">
              <InfoRow
                highlight
                label={guideline?.is_reservation ? "예약 날짜" : "체험기간"}
              >
                {guideline?.is_reservation ? (
                  dayjs
                    .tz(`${guideline.reservation_dtm}`, TZ)
                    .format("YYYY년 M월 D일 ")
                ) : (
                  <div className="text-gray-800">
                    {guideline.review_start_date} ~ {guideline.review_end_date}
                  </div>
                )}
              </InfoRow>

              <InfoRow label="작성기간">{guideline?.review_period}</InfoRow>
              <InfoRow label="캠페인">
                {removeBrackets(guideline?.title ?? "")}
              </InfoRow>
              <InfoRow label="주소">{guideline?.address}</InfoRow>

              <InfoRow label="방문시간" highlight>
                {guideline?.is_reservation &&
                guideline?.reservation_dtm &&
                guideline?.reservation_time
                  ? dayjs
                      .tz(
                        `${guideline.reservation_dtm}T${guideline.reservation_time}`,
                        TZ
                      )
                      .format("A h시 mm분")
                  : guideline?.visit_time}
              </InfoRow>

              <InfoRow label="제공내역">{guideline?.provision_details}</InfoRow>
              <InfoRow label="예약번호">{guideline?.reservation_no}</InfoRow>
              <InfoRow label="체험방법">{guideline?.experience_method}</InfoRow>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex items-start ml-2">
        <div className="flex flex-col items-center mr-2">
          <div
            className={`w-6 h-6 text-xs rounded-full flex items-center justify-center ${
              guideline?.is_attend ? "bg-[#46B6D0]" : "bg-gray-200"
            } text-white`}
          >
            2
          </div>
        </div>
        <p
          className={`pt-1 text-sm ${
            guideline?.is_attend ? "text-[#46B6D0]" : "text-gray-300"
          }`}
        >
          체험완료
        </p>
      </div>

      {/* Bottom Navigation */}
      <div className="max-w-xl mx-auto fixed z-20 bottom-0 left-0 right-0 bg-white border-t justify-between px-4 py-3 flex gap-2">
        <div
          onClick={onPrev}
          className="flex pr-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center cursor-pointer"
        >
          <ChevronLeft />
          <div className="font-semibold">이전</div>
        </div>

        {!guideline?.is_attend ? (
          <Button
            onClick={() => setShowModal(true)}
            className="rounded-full w-3/4 bg-[#0F1E6C] text-white text-base h-11"
          >
            이용하기
          </Button>
        ) : (
          <div
            onClick={onNext}
            className="flex ps-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center cursor-pointer"
          >
            <div className="font-semibold">다음</div>
            <ChevronRight />
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showModal && (
        <div className="text-center fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md relative">
            <X
              className="absolute right-4 top-4 w-6 h-6 text-gray-800 cursor-pointer"
              onClick={() => setShowModal(false)}
            />
            <h2 className="text-lg font-bold mb-4 mt-4">[ 초대권 ]</h2>
            <hr />
            <p className="text-gray-800 font-semibold mt-6">
              [{guideline?.name}] 님을 초대합니다.
            </p>
            <div className="py-4">
              {guideline?.reservation_dtm ? (
                <p className="text-gray-500 mb-4 text-sm">
                  예약일시{" "}
                  {dayjs
                    .tz(
                      `${guideline.reservation_dtm}T${guideline.reservation_time}`,
                      TZ
                    )
                    .format("M월 D일 A h시 mm분")}
                </p>
              ) : (
                <p className="text-gray-500 mb-4 text-sm">
                  사용일자 {startDate?.format("YYYY년 M월 D일")} ~{" "}
                  {endDate?.format("YYYY년 M월 D일")}
                </p>
              )}
            </div>
            <button
              onClick={handleAttend}
              className="px-4 py-3 w-full text-white bg-[#0F1E6C] text-sm rounded-full font-semibold"
            >
              초대권 사용
            </button>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md relative text-center">
            <X
              className="absolute right-4 top-4 w-6 h-6 text-gray-800 cursor-pointer"
              onClick={() => setShowContactModal(false)}
            />
            <h2 className="text-base font-bold mt-4 mb-4 pb-4 border-b">
              [ 업체와 연락이 어려우신가요? ]
            </h2>
            <p className="text-sm text-gray-700 mb-1">
              광고주와 연락이 어려우신가요?
            </p>
            <p className="text-sm font-bold mb-4">
              위유팀에서 도와드리겠습니다.
            </p>

            <textarea
              rows={4}
              value={faqReason}
              onChange={(e) => setFaqReason(e.target.value)}
              placeholder="추가 설명 입력해주세요. 위유 관리팀에서 도와드리겠습니다."
              className="w-full p-3 border border-gray-300 rounded-lg text-xs mb-4 resize-none focus:outline-none focus:ring-1 focus:ring-[#0F1E6C]"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setShowContactModal(false)}
                className="w-1/2 py-2 rounded-full text-sm bg-gray-200 text-gray-500"
              >
                취소
              </button>
              <button
                onClick={sendFAQ}
                disabled={faqLoading}
                className="w-1/2 py-2 rounded-full text-sm bg-[#0F1E6C] text-white font-semibold"
              >
                {faqLoading ? "요청 중..." : "관리자에게 요청"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="text-center fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md">
            <h2 className="text-lg font-bold mb-4">확인</h2>
            <hr />
            <p className="mb-6 pt-6 leading-7 text-sm">
              초대권 사용 처리가 됩니다. <br />
              업체측에서 사용처리 부탁드립니다.
            </p>
            <div className="flex gap-3 text-sm">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 w-1/2 border border-gray-300 rounded-full"
              >
                취소
              </button>
              <button
                onClick={confirmAttend}
                disabled={loading}
                className="px-4 py-2 w-1/2 bg-[#0F1E6C] text-white rounded-full"
              >
                {loading ? "처리 중..." : "초대권 사용"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoRow = ({
  label,
  children,
  highlight = false,
}: {
  label: string;
  children: React.ReactNode;
  highlight?: boolean;
}) => (
  <div className="flex items-start">
    <div className="w-1/4 min-w-[80px] text-gray-400">{label}</div>
    <div
      className={`w-3/4 ${
        highlight ? "font-semibold text-red-500" : "text-gray-800"
      }`}
    >
      {children}
    </div>
  </div>
);

export default Attend;
